import { CreateStaffTicketCommentRequest } from "@signalist/shared-types";

import { ReplyOutcome } from "@/pages/Private/pages/Support/pages/TicketDetail/hooks/useTicketReply";
import {
	useAddStaffTicketCommentMutation,
	useDispatchStaffTicketReplyMutation,
	useUploadStaffTicketAttachmentMutation,
} from "@/pages/Private/redux/tickets/tickets-admin.api";

/**
 * The staff twin of `useTicketReply`: post the reply or note (with "Then set" on a reply), then
 * upload each file against the MESSAGE row the API answered with (PREC-3940 decision 1), then ask
 * the API to send the reply's support mail (PREC-4088). A failed upload never discards the comment;
 * the caller reports which file did not make it.
 *
 * The dispatch is a third request rather than something the comment POST does, because the files
 * only exist once step two has run — a mail sent inside step one could not carry them, and did
 * not: an agent's credit note was stored against the comment and never left the building. It runs
 * even when an upload failed, so the customer still gets the answer plus the note naming what did
 * not make it, and even when there were no files at all, because a reply with no attachment needs
 * sending just the same.
 *
 * A dispatch that fails is deliberately NOT reported as a failed reply. The comment is written and
 * the server sweeps anything still unsent within five minutes, so telling the agent their reply
 * failed would be wrong and would invite them to send it twice.
 */
export const useStaffTicketReply = (ticketId: number) => {
	const [addComment, { isLoading: isPosting }] = useAddStaffTicketCommentMutation();
	const [uploadAttachment, { isLoading: isUploading }] = useUploadStaffTicketAttachmentMutation();
	const [dispatchReply, { isLoading: isDispatching }] = useDispatchStaffTicketReplyMutation();

	const send = async (
		body: CreateStaffTicketCommentRequest,
		files: File[]
	): Promise<ReplyOutcome> => {
		let commentId: number;

		try {
			commentId = (await addComment({ ticketId, ...body }).unwrap()).comment.id;
		} catch {
			return { ok: false };
		}

		let failedAttachment: string | undefined;

		for (const file of files) {
			try {
				await uploadAttachment({ ticketId, commentId, file }).unwrap();
			} catch {
				failedAttachment = file.name;
				break;
			}
		}

		try {
			await dispatchReply({ ticketId, commentId }).unwrap();
		} catch {
			// The sweep will send it; see this hook's doc comment.
		}

		return { ok: true, failedAttachment };
	};

	return { send, isSending: isPosting || isUploading || isDispatching };
};
